<template>
  <div class="fade-in">
    <div class="columns is-multiline">
      <div class="column is-full">
        <div class="is-pulled-left">
          <h1 class="title mr2">Patient Queue</h1>
        </div>
        <div class="is-pulled-left search-bar">
          <div class="field" style="vertical-align: bottom;">
            <p
              :class="[
                'control',
                'has-icons-left',
                { 'is-loading': searchLoading }
              ]"
            >
              <input
                class="input search"
                type="search"
                v-model="search"
                placeholder="Search Patients"
                @search="doSearch"
              />
              <span v-if="!searchLoading" class="icon is-small is-right">
                <i class="fa fa-search"></i>
              </span>
            </p>
          </div>
        </div>
        <div class="is-clearfix"></div>
      </div>
      <div class="column is-full count">
        Showing
        <span class="patient-count">{{ patientCount() }}</span>
        out of
        <span class="patient-total">{{ patientTotal() }}</span>
        patients in
        <select
          v-model="interviewKind"
          @change="interviewFilter"
          class="interview-select"
        >
          <option value="null">All interviews</option>
          <option value="0">Schedule interviews</option>
          <option value="1">PHQ-9 interviews</option>
        </select>
      </div>
    </div>

    <div class="columns is-mobile is-multiline mt3">
      <div
        class="column is-one-quarter-widescreen is-one-half-desktop is-full-mobile is-half-tablet mb1"
      >
        <div class="card pointer queue-stats" @click="filter(0)">
          <div class="card-content columns is-mobile is-multiline">
            <div class="column is-narrow is-pulled-left">
              <i class="fa fa-circle status status-nuw"></i>
            </div>
            <div class="column is-pulled-left">
              <h2>
                <span class="bold">{{ counts.nuw }}</span>
                <span class="regular">New</span>
              </h2>
              <div class="filter-link">View all new patients</div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="column is-one-quarter-widescreen is-one-half-desktop is-full-mobile is-half-tablet mb1"
      >
        <div class="card pointer queue-stats" @click="filter(1)">
          <div class="card-content columns is-mobile is-multiline">
            <div class="column is-narrow is-pulled-left">
              <i class="fa fa-circle status status-pending"></i>
            </div>
            <div class="column is-pulled-left">
              <h2>
                <span class="bold">{{ counts.pending }}</span>
                <span class="regular">Pending</span>
              </h2>
              <div class="filter-link">View all pending patients</div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="column is-one-quarter-widescreen is-one-half-desktop is-full-mobile is-half-tablet mb1"
      >
        <div class="card pointer queue-stats" @click="filter(2)">
          <div class="card-content columns is-mobile is-multiline">
            <div class="column is-narrow is-pulled-left">
              <i class="fa fa-circle status status-scheduled"></i>
            </div>
            <div class="column is-pulled-left">
              <h2>
                <span class="bold">{{ counts.scheduled }}</span>
                <span class="regular">Scheduled</span>
              </h2>
              <div class="filter-link">View all scheduled patients</div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="column is-one-quarter-widescreen is-one-half-desktop is-full-mobile is-half-tablet mb1"
      >
        <div class="card pointer queue-stats" @click="filter(3)">
          <div class="card-content columns is-mobile is-multiline">
            <div class="column is-narrow is-pulled-left">
              <i class="fa fa-circle status status-all"></i>
            </div>
            <div class="column is-pulled-left">
              <h2>
                <span class="bold">{{ counts.archived }}</span>
                <span class="regular">Archived</span>
              </h2>
              <div class="filter-link">View all archived patients</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <table class="table is-responsive table-queue">
      <thead>
        <tr class="vmiddle">
          <th>&nbsp;</th>
          <col-sort
            sort="created_at"
            :sorted="sorted"
            :sort-dir="sortDir"
            :click="sortClick"
            >Submitted</col-sort
          >
          <col-sort
            sort="interview_name"
            :sorted="sorted"
            :sort-dir="sortDir"
            :click="sortClick"
            class="is-small"
            >Interview Name</col-sort
          >
          <col-sort
            sort="location"
            :sorted="sorted"
            :sort-dir="sortDir"
            :click="sortClick"
            class="is-small"
            >Schedule Location</col-sort
          >
          <col-sort
            sort="last_name"
            :sorted="sorted"
            :sort-dir="sortDir"
            :click="sortClick"
            >Patient Name</col-sort
          >
          <col-sort
            sort="email"
            :sorted="sorted"
            :sort-dir="sortDir"
            :click="sortClick"
            >Email</col-sort
          >
          <col-sort
            sort="phone"
            :sorted="sorted"
            :sort-dir="sortDir"
            :click="sortClick"
            >Phone</col-sort
          >
          <col-sort
            sort="city"
            :sorted="sorted"
            :sort-dir="sortDir"
            :click="sortClick"
            >City</col-sort
          >
          <col-sort
            sort="state"
            :sorted="sorted"
            :sort-dir="sortDir"
            :click="sortClick"
            >State</col-sort
          >
          <col-sort
            sort="zip"
            :sorted="sorted"
            :sort-dir="sortDir"
            :click="sortClick"
            >Zip</col-sort
          >
          <col-sort
            sort="score"
            :sorted="sorted"
            :sort-dir="sortDir"
            :click="sortClick"
            class="has-text-centered"
            >Patient Score</col-sort
          >
          <col-sort
            sort="interview"
            :sorted="sorted"
            :sort-dir="sortDir"
            :click="sortClick"
            class="has-text-centered"
            >Type</col-sort
          >
        </tr>
      </thead>
      <tbody>
        <tr
          v-if="!loading"
          v-for="patient in patients"
          :key="patient.id"
          @click="viewPatient(patient)"
          class="pointer"
        >
          <td class="has-text-centered">
            <i
              :class="[
                'fa',
                'fa-circle',
                'status',
                `status-${patient.attributes.status}`
              ]"
              :title="statusList[patient.attributes.status]"
            ></i>
          </td>
          <td>{{ patient.attributes.createdAt | datetime }}</td>
          <td>{{ patient.attributes.interviewName }}</td>
          <td>{{ patient.attributes.location }}</td>
          <td>
            {{ patient.attributes.firstName }} {{ patient.attributes.lastName }}
          </td>
          <td>{{ patient.attributes.email }}</td>
          <td>{{ patient.attributes.phone }}</td>
          <td>{{ patient.attributes.city }}</td>
          <td>{{ patient.attributes.state }}</td>
          <td>{{ patient.attributes.zip }}</td>
          <td class="has-text-centered">
            <span class="table-patient-score">
              {{ patient.attributes.score
              }}{{ patient.attributes.interviewKind === "Schedule" ? "%" : "" }}
            </span>
          </td>
          <td class="table-last-td">{{ patient.attributes.interviewKind }}</td>
        </tr>
        <tr v-if="loading">
          <td colspan="11" class="has-text-centered">
            <i class="fa fa-refresh fa-spin fa-3x"></i>
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <td colspan="11" class="has-text-centered mv2">
            <button
              v-if="morePages"
              type="button"
              :class="['button', 'is-primary', { 'is-loading': loading }]"
              @click="loadMore"
            >
              Load More
            </button>
          </td>
        </tr>
      </tfoot>
    </table>
    <div class="is-clearfix"></div>
  </div>
</template>

<script>
import { camelize } from "humps";
import PatientQueueService from "@/services/PatientQueueService";
import ColSort from "@/components/patient/ColSort";
import helpers from "./helpers";

export default {
  name: "patient-queue-list",

  props: ["filters"],

  components: {
    ColSort
  },

  data() {
    return {
      loading: false,
      sorted: "created_at",
      sortDir: "-",
      status: 0,
      search: null,
      searchLoading: false,
      interviewKind: null,
      currentPage: 1,
      fieldTypes: {
        createdAt: "datetime",
        interviewName: "string",
        location: "string",
        lastName: "string",
        email: "string",
        phone: "string",
        city: "string",
        state: "string",
        zip: "string",
        score: "integer",
        interview: "string"
      }
    };
  },

  computed: {
    patients() {
      let patients = this.$store.getters["patientQueue/queue"];
      let sortDir = "asc";
      if (this.sortDir == "-") {
        sortDir = "desc";
      }

      let currentStatus = helpers.getStatusWithIndex(
        this.status,
        this.statusList
      );

      patients = patients.filter(
        patient => patient.attributes.status == currentStatus
      );

      let attr = camelize(this.sorted);
      patients = patients.sort((a, b) => {
        let valA = a.attributes[attr],
          valB = b.attributes[attr],
          type = this.fieldTypes[attr] || "string";
        if (type == "string") {
          if (valA < valB) {
            return sortDir == "desc" ? 1 : -1;
          }
          if (valA > valB) {
            return sortDir == "desc" ? -1 : 1;
          }
          return 0;
        } else if (type == "integer") {
          if (sortDir == "desc") {
            return valB - valA;
          } else {
            return valA - valB;
          }
        } else if (type == "datetime") {
          if (sortDir == "desc") {
            return new Date(valB) - new Date(valA);
          } else {
            return new Date(valA) - new Date(valB);
          }
        }
      });

      return patients;
    },
    practice() {
      return this.$store.getters["practice/current"];
    },
    counts() {
      return this.$store.getters["patientQueue/counts"];
    },
    statusList() {
      return this.$store.getters["patientQueue/statusList"];
    },
    nextPage() {
      return this.$store.getters["patientQueue/nextPage"];
    },
    currentUser() {
      return this.$store.getters["currentUser"];
    },
    morePages() {
      return this.$store.getters["patientQueue/morePages"];
    }
  },

  methods: {
    viewPatient(patient) {
      this.$router.push({ name: "patient-edit", params: { id: patient.id } });
    },
    patientCount() {
      return this.patients === undefined
        ? 0
        : Object.keys(this.patients).length;
    },
    patientTotal() {
      let total = 0;

      switch (this.status) {
        case 0:
        case 1:
          total = this.counts.nuw + this.counts.pending;
          break;
        case 2:
          total = this.counts.scheduled;
          break;
        default:
          total = this.counts.archived;
          break;
      }

      return total;
    },
    loadMore() {
      this.loading = true;
      this.loadQueue(this.nextPage);
    },

    loadQueue(pageNumber = 1) {
      if (this.currentUser.id == "" || this.currentUser.id == null) return;
      let location = this.currentUser.location || "";

      this.loading = true;
      PatientQueueService.getCounts({ location: location });
      const params = {
        sortDir: this.sortDir,
        sorted: this.sorted,
        status: this.status,
        pageNumber: pageNumber,
        search: this.search,
        interview: this.interviewKind,
        location: location
      };
      PatientQueueService.load(params).then(() => {
        this.loading = false;
        this.searchLoading = false;
        this.currentPage = pageNumber;
      });
    },
    sortClick(field) {
      if (this.sorted === field) {
        if (this.sortDir === "") {
          this.sortDir = "-";
        } else {
          this.sortDir = "";
        }
      } else {
        this.sorted = field;
        this.sortDir = "-";
      }
      this.loadQueue(this.currentPage);
    },
    filter(status) {
      this.status = status;
      this.$store.dispatch("patientQueue/resetPage");
      this.loadQueue();
    },
    interviewFilter() {
      this.$store.dispatch("patientQueue/resetPage");
      this.loadQueue();
    },
    doSearch() {
      this.searchLoading = true;
      this.loadQueue();
    }
  },

  watch: {
    practice(newValue, oldValue) {
      if (newValue.subdomain !== oldValue.subdomain) {
        this.loadQueue();
      }
    },
    currentUser(newVal, oldVal) {
      this.loadQueue(this.currentPage);
    }
  },

  mounted() {
    // force the new patients view
    if (!this.$props.filters) {
      this.filter(0);
    }
  }
};
</script>

<style lang="scss" scoped>
.is-sorted {
  background: #ddd;
}

tr.vmiddle th {
  vertical-align: middle;
}

.interview-select {
  font-family: "Avenir", "Helvetica", Arial, sans-serif;
  background-color: transparent;
  border: 1px solid #e1e4e5;
  color: #1b2437;
  border-radius: 5px;
  box-shadow: none;
  font-size: 1.14rem;
}
</style>
